import React, { useState, useEffect, useRef } from 'react';
import './MapPicker.css';

export default function MapPicker({ onLocationSelect, initialLocation, searchQuery, onSearchChange, pincode }) {
  const mapRef = useRef(null);
  const scriptRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(initialLocation || { lat: 26.7588, lng: 83.3697 }); // Default: Gorakhpur
  const [selectedAddress, setSelectedAddress] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Handle pincode search
  useEffect(() => {
    if (pincode && pincode.length === 6 && /^\d{6}$/.test(pincode) && mapInstanceRef.current) {
      const geocoder = new window.google.maps.Geocoder();
      const mapInstance = mapInstanceRef.current;
      const markerInstance = markerInstanceRef.current;
      
      // Search for pincode in India
      geocoder.geocode({ 
        address: pincode,
        region: 'in',
        componentRestrictions: { 
          country: 'in',
          postalCode: pincode
        }
      }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const newLocation = {
            lat: location.lat(),
            lng: location.lng()
          };
          setCurrentLocation(newLocation);
          mapInstance.setCenter(newLocation);
          mapInstance.setZoom(15);
          if (markerInstance) {
            markerInstance.setPosition(newLocation);
          }
          geocodePosition(newLocation, mapInstance);
        } else {
          // Try alternative search with "India" suffix
          geocoder.geocode({ 
            address: `${pincode}, India`,
            region: 'in',
            componentRestrictions: { country: 'in' }
          }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              const newLocation = {
                lat: location.lat(),
                lng: location.lng()
              };
              setCurrentLocation(newLocation);
              mapInstance.setCenter(newLocation);
              mapInstance.setZoom(15);
              if (markerInstance) {
                markerInstance.setPosition(newLocation);
              }
              geocodePosition(newLocation, mapInstance);
            }
          });
        }
      });
    }
  }, [pincode]);

  useEffect(() => {
    let isMounted = true;
    
    // Load Google Maps script
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    // Debug logging
    console.log('🗺️ MapPicker: Checking for Google Maps API key...');
    console.log('API Key exists:', !!apiKey);
    console.log('API Key length:', apiKey ? apiKey.length : 0);
    
    if (!apiKey) {
      console.warn('⚠️ Google Maps API key not found. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file');
      console.warn('⚠️ Note: You must restart the React dev server after adding/changing .env file');
      if (isMounted) {
        setMapError({
          type: 'no-api-key',
          message: 'Map requires Google Maps API key',
          details: 'Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file and restart the server'
        });
        setIsInitializing(false);
      }
      return;
    }
    
    console.log('✅ API Key found, loading Google Maps...');
    
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Google Maps script loaded successfully');
        if (isMounted && mapRef.current) {
          initializeMap();
        }
      };
      script.onerror = (error) => {
        console.error('❌ Failed to load Google Maps script', error);
        console.error('API Key used:', apiKey.substring(0, 10) + '...');
        if (isMounted) {
          setMapError({
            type: 'load-error',
            message: 'Failed to load Google Maps',
            details: 'Please check: API key is correct, Maps JavaScript API is enabled, Places API is enabled, and API key has proper restrictions'
          });
          setIsInitializing(false);
        }
      };
      scriptRef.current = script;
      document.head.appendChild(script);
    } else {
      if (isMounted && mapRef.current) {
        initializeMap();
      }
    }

    return () => {
      isMounted = false;
      
      // Cleanup: Remove map listeners using refs
      // Don't set state during cleanup as component might be unmounting
      if (markerInstanceRef.current && window.google?.maps?.event) {
        try {
          window.google.maps.event.clearInstanceListeners(markerInstanceRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      if (mapInstanceRef.current && window.google?.maps?.event) {
        try {
          window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      
      // Clear refs
      markerInstanceRef.current = null;
      mapInstanceRef.current = null;
    };
  }, []);

  const initializeMap = () => {
    if (!mapRef.current) {
      console.error('❌ Map container ref is not available');
      return;
    }
    
    if (!window.google || !window.google.maps) {
      console.error('❌ Google Maps API is not loaded');
      setMapError({
        type: 'init-error',
        message: 'Google Maps API not available',
        details: 'Please refresh the page and try again'
      });
      setIsInitializing(false);
      return;
    }
    
    console.log('🗺️ Initializing map...');
    
    try {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: currentLocation,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      const mapMarker = new window.google.maps.Marker({
        position: currentLocation,
        map: mapInstance,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
      });

      // Get address for initial location
      geocodePosition(currentLocation, mapInstance);

      // Handle marker drag
      mapMarker.addListener('dragend', (e) => {
        const newPosition = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        };
        setCurrentLocation(newPosition);
        geocodePosition(newPosition, mapInstance);
      });

      // Handle map click
      mapInstance.addListener('click', (e) => {
        const newPosition = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        };
        mapMarker.setPosition(newPosition);
        setCurrentLocation(newPosition);
        geocodePosition(newPosition, mapInstance);
      });

      // Store in both state and refs for cleanup
      mapInstanceRef.current = mapInstance;
      markerInstanceRef.current = mapMarker;
      setMap(mapInstance);
      setMarker(mapMarker);
      setMapLoaded(true);
      setMapError(null);
      setIsInitializing(false);
      console.log('✅ Map initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing map:', error);
      setMapError({
        type: 'init-error',
        message: 'Error initializing map',
        details: error.message || 'Please refresh the page and try again'
      });
      setIsInitializing(false);
    }
  };

  const geocodePosition = (position, mapInstance) => {
    if (!window.google?.maps?.Geocoder) {
      console.error('Geocoder not available');
      return;
    }
    
    const geocoder = new window.google.maps.Geocoder();
    // Restrict geocoding to India region
    geocoder.geocode({ 
      location: position,
      region: 'in' // Restrict to India
    }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;
        setSelectedAddress(address);
        
        // Parse address components
        const addressComponents = parseAddressComponents(results[0].address_components);
        
        // Notify parent component
        if (onLocationSelect) {
          onLocationSelect({
            ...position,
            address: address,
            ...addressComponents
          });
        }
      }
    });
  };

  const parseAddressComponents = (components) => {
    const parsed = {
      street: '',
      city: '',
      state: '',
      pincode: '',
      area: ''
    };

    components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number') || types.includes('route')) {
        parsed.street = (parsed.street + ' ' + component.long_name).trim();
      }
      
      if (types.includes('locality') || types.includes('sublocality')) {
        parsed.area = component.long_name;
        if (!parsed.city) parsed.city = component.long_name;
      }
      
      if (types.includes('administrative_area_level_2')) {
        parsed.city = component.long_name;
      }
      
      if (types.includes('administrative_area_level_1')) {
        parsed.state = component.long_name;
      }
      
      if (types.includes('postal_code')) {
        parsed.pincode = component.long_name;
      }
    });

    return parsed;
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(newLocation);
          if (map) {
            map.setCenter(newLocation);
            map.setZoom(15);
            if (marker) {
              marker.setPosition(newLocation);
            }
            geocodePosition(newLocation, map);
            // Clear search query after geolocation to enable fresh search
            if (onSearchChange) {
              onSearchChange('');
            }
          }
        },
        (error) => {
          alert('Unable to get your location. Please enable location services.');
        }
      );
    }
  };

  const handleSearch = () => {
    if (!map || !searchQuery) return;

    const geocoder = new window.google.maps.Geocoder();
    // Global search - no country restrictions
    geocoder.geocode({ 
      address: searchQuery
    }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const newLocation = {
          lat: location.lat(),
          lng: location.lng()
        };
        setCurrentLocation(newLocation);
        map.setCenter(newLocation);
        map.setZoom(15);
        if (marker) {
          marker.setPosition(newLocation);
        }
        geocodePosition(newLocation, map);
      } else {
        alert('Location not found. Please try a different search.');
      }
    });
  };

  return (
    <div className="map-picker-container">
      <div className="map-search-bar">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="map-search-input"
            placeholder="Search for location..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <button
            className="search-btn"
            onClick={handleSearch}
            disabled={!searchQuery || !map}
            title="Search location"
          >
            Search
          </button>
          {searchQuery && (
            <button
              className="clear-search-btn"
              onClick={() => onSearchChange('')}
            >
              ×
            </button>
          )}
        </div>
        <button
          className="current-location-btn"
          onClick={handleCurrentLocation}
          title="Go to current location"
        >
          <span className="compass-icon">🧭</span>
          <span className="btn-text">Go to current location</span>
        </button>
      </div>

      <div className="map-container-wrapper">
        <div ref={mapRef} className="map-container"></div>
        {mapError && (
          <div className="map-error">
            <p className="error-message">{mapError.message}</p>
            <p className="error-details">{mapError.details}</p>
            {mapError.type === 'no-api-key' && (
              <p className="error-note">⚠️ Don't forget to restart the React dev server after adding the key!</p>
            )}
          </div>
        )}
        {isInitializing && !mapError && !mapLoaded && (
          <div className="map-loading">
            <p>Loading map...</p>
          </div>
        )}
      </div>

      {selectedAddress && (
        <div className="selected-location-info">
          <div className="location-label">Delivering your order to</div>
          <div className="location-address">
            <span className="location-icon">📍</span>
            <div className="address-text">
              <div className="address-primary">{selectedAddress.split(',')[0]}</div>
              <div className="address-secondary">
                {selectedAddress.split(',').slice(1).join(',').trim()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
